import { useState, useMemo, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Card,
  Descriptions,
  Button,
  Space,
  Tag,
  message,
  Table,
  Input,
  Popconfirm,
  Alert,
  Spin,
  Empty,
  Select,
  Breadcrumb,
  Row,
  Col,
} from 'antd'
import {
  EditOutlined,
  PlusOutlined,
  MinusOutlined,
  SearchOutlined,
  FlagOutlined,
} from '@ant-design/icons'
import { debounce } from 'lodash-es'
import type { ColumnsType } from 'antd/es/table'
import {
  getFeatureFlagById,
  getFeatureFlagTargets,
  addFeatureFlagTargets,
  removeFeatureFlagTargets,
} from '@/api/features'
import type { FeatureFlagTarget } from '@/types/feature'
import { useAuth } from '@/hooks/useAuth'
import { usersApi } from '@/api/users'
import { hospitalsApi } from '@/api/hospitals'
import { usePageHeader } from '@/hooks/usePageHeader'
import { getApiErrorMessage } from '@/utils/error'
import { PageLoading } from '@/components/PageLoading'

const { Search } = Input

export const FeatureDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [selectedEnabledRows, setSelectedEnabledRows] = useState<string[]>([])
  const [searchEnabled, setSearchEnabled] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTargetsToAdd, setSelectedTargetsToAdd] = useState<string[]>([])

  // Check if user can modify targets
  const canModifyTargets = user?.role === 'ADMIN' || user?.role === 'SUPPORT'
  const isAdmin = user?.role === 'ADMIN'

  // Fetch feature flag details
  const { data: flagData, isLoading: flagLoading } = useQuery({
    queryKey: ['features', id],
    queryFn: () => getFeatureFlagById(id!),
    enabled: !!id,
  })

  const flag = flagData?.data

  // Handle edit navigation
  const handleEdit = useCallback(() => {
    if (flag) {
      navigate(`/features/${id}/edit`)
    }
  }, [flag, id, navigate])

  // Memoize page header config - hooks must be at top level, before any returns
  const pageHeaderConfig = useMemo(() => {
    if (!flag) {
      return {} // Empty config while loading or if flag not found
    }

    return {
      breadcrumb: (
        <Breadcrumb
          items={[
            {
              title: (
                <a href="#" onClick={(e) => { e.preventDefault(); navigate('/features'); }}>
                  <FlagOutlined /> Feature Flags
                </a>
              ),
            },
            {
              title: flag.label,
            },
          ]}
        />
      ),
      title: <h1 style={{ margin: 0 }}>{flag.label}</h1>,
      extra: isAdmin ? (
        <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
          Edit
        </Button>
      ) : undefined,
    }
  }, [flag, navigate, handleEdit, isAdmin])

  // Set page header in main AppLayout header (must be called unconditionally)
  usePageHeader(pageHeaderConfig)

  // Debounced search for typeahead
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchQuery(value)
      }, 300),
    []
  )

  // Search for users (when scope is 'user')
  const { data: usersSearchData, isLoading: isSearchingUsers } = useQuery({
    queryKey: ['users-search', searchQuery],
    queryFn: () => usersApi.getUsers({ search: searchQuery, limit: 20, active: true }),
    enabled: flag?.scope === 'user' && searchQuery.length >= 2,
  })

  // Search for hospitals (when scope is 'facility')
  const { data: hospitalsSearchData, isLoading: isSearchingHospitals } = useQuery({
    queryKey: ['hospitals-search', searchQuery],
    queryFn: () => hospitalsApi.searchHospitals(searchQuery, 20),
    enabled: flag?.scope === 'facility' && searchQuery.length >= 2,
  })

  // Fetch targets
  const { data: targetsData, isLoading: targetsLoading } = useQuery({
    queryKey: ['features', id, 'targets'],
    queryFn: () => getFeatureFlagTargets(id!),
    enabled: !!id,
  })

  // Add targets mutation
  const addTargetsMutation = useMutation({
    mutationFn: (targetIds: string[]) => addFeatureFlagTargets(id!, targetIds),
    onSuccess: (_, targetIds) => {
      message.success(
        `Successfully added ${targetIds.length} target${targetIds.length !== 1 ? 's' : ''}`
      )
      queryClient.invalidateQueries({ queryKey: ['features', id] })
      setSelectedTargetsToAdd([])
      setSearchQuery('')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to add targets'))
    },
  })

  // Remove targets mutation
  const removeTargetsMutation = useMutation({
    mutationFn: (targetIds: string[]) => removeFeatureFlagTargets(id!, targetIds),
    onSuccess: (_, targetIds) => {
      message.success(
        `Successfully removed ${targetIds.length} target${targetIds.length !== 1 ? 's' : ''}`
      )
      queryClient.invalidateQueries({ queryKey: ['features', id] })
      setSelectedEnabledRows([])
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to remove targets'))
    },
  })

  // Filter enabled targets based on search
  const filteredEnabled =
    targetsData?.data.enabled.filter(
      (target) =>
        target.name.toLowerCase().includes(searchEnabled.toLowerCase()) ||
        target.email?.toLowerCase().includes(searchEnabled.toLowerCase())
    ) || []

  // Get enabled target IDs to exclude from search results
  const enabledTargetIds = new Set(targetsData?.data.enabled.map((t) => t.id) || [])

  // Build Select options from search results
  const searchOptions = useMemo(() => {
    if (flag?.scope === 'user') {
      return (usersSearchData?.data || [])
        .filter((u) => !enabledTargetIds.has(u.id))
        .map((u) => ({
          value: u.id,
          label: `${u.first_name} ${u.last_name} (${u.email})`,
        }))
    } else if (flag?.scope === 'facility') {
      return (hospitalsSearchData?.data || [])
        .filter((h) => !enabledTargetIds.has(h.id))
        .map((h) => ({
          value: h.id,
          label: `${h.name} - ${h.city}, ${h.state}`,
        }))
    }
    return []
  }, [flag?.scope, usersSearchData, hospitalsSearchData, enabledTargetIds])

  const targetColumns: ColumnsType<FeatureFlagTarget> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    ...(flag?.scope === 'user'
      ? [
          {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: (a: FeatureFlagTarget, b: FeatureFlagTarget) =>
              (a.email || '').localeCompare(b.email || ''),
          },
        ]
      : []),
  ]

  if (flagLoading) {
    return <PageLoading />
  }

  if (!flag) {
    return <Alert message="Feature flag not found" type="error" showIcon />
  }

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Feature Key">
            <code>{flag.feature_key}</code>
          </Descriptions.Item>
          <Descriptions.Item label="Label">{flag.label}</Descriptions.Item>
          <Descriptions.Item label="Scope">
            <Tag color={flag.scope === 'user' ? 'blue' : 'green'}>
              {flag.scope === 'user' ? 'User' : 'Facility'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Type">
            <Tag color={flag.flag_type === 'application' ? 'purple' : 'orange'}>
              {flag.flag_type === 'application' ? 'Application' : 'Config'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Default Value">
            <Tag color={flag.default_value ? 'success' : 'default'}>
              {flag.default_value ? 'Enabled' : 'Disabled'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Total Targets">
            <Tag color="cyan">{flag.targetCount || 0}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            {new Date(flag.createdAt).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            {new Date(flag.updatedAt).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={`Enabled Targets (${filteredEnabled.length})`}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {canModifyTargets && (
            <Row gutter={16}>
              <Col flex="auto">
                <Select
                  mode="multiple"
                  placeholder={`Search and add ${flag?.scope === 'user' ? 'users' : 'hospitals'}...`}
                  value={selectedTargetsToAdd}
                  onChange={setSelectedTargetsToAdd}
                  onSearch={(value) => {
                    if (value.length >= 2) {
                      debouncedSearch(value)
                    }
                  }}
                  options={searchOptions}
                  loading={isSearchingUsers || isSearchingHospitals}
                  filterOption={false}
                  showSearch
                  style={{ width: '100%' }}
                  notFoundContent={
                    searchQuery.length < 2 ? (
                      <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                        Type at least 2 characters to search
                      </div>
                    ) : isSearchingUsers || isSearchingHospitals ? (
                      <div style={{ textAlign: 'center', padding: '8px' }}>
                        <Spin size="small" />
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '8px', color: '#999' }}>
                        No {flag?.scope === 'user' ? 'users' : 'hospitals'} found
                      </div>
                    )
                  }
                />
              </Col>
              <Col>
                {selectedTargetsToAdd.length > 10 ? (
                  <Popconfirm
                    title="Add multiple targets"
                    description={
                      <>
                        <p>
                          Add {selectedTargetsToAdd.length} target
                          {selectedTargetsToAdd.length !== 1 ? 's' : ''} to this feature?
                        </p>
                        <p style={{ color: '#888', fontSize: '12px', marginTop: '8px' }}>
                          This is a bulk operation. Consider testing with fewer targets first.
                        </p>
                      </>
                    }
                    onConfirm={() => addTargetsMutation.mutate(selectedTargetsToAdd)}
                    okText="Yes, Add All"
                    cancelText="Cancel"
                  >
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      disabled={selectedTargetsToAdd.length === 0}
                      loading={addTargetsMutation.isPending}
                    >
                      Add Selected ({selectedTargetsToAdd.length})
                    </Button>
                  </Popconfirm>
                ) : (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={selectedTargetsToAdd.length === 0}
                    onClick={() => addTargetsMutation.mutate(selectedTargetsToAdd)}
                    loading={addTargetsMutation.isPending}
                  >
                    Add Selected ({selectedTargetsToAdd.length})
                  </Button>
                )}
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col flex="auto">
              <Search
                placeholder="Search enabled targets"
                allowClear
                enterButton={<SearchOutlined />}
                onSearch={setSearchEnabled}
              />
            </Col>
            <Col>
              {canModifyTargets && (
                <Popconfirm
                  title="Remove selected targets"
                  description={
                    <>
                      <p>
                        Are you sure you want to remove {selectedEnabledRows.length} target
                        {selectedEnabledRows.length !== 1 ? 's' : ''}?
                      </p>
                      <p style={{ color: '#888', fontSize: '12px', marginTop: '8px' }}>
                        They can be re-enabled later using the search above.
                      </p>
                    </>
                  }
                  onConfirm={() => removeTargetsMutation.mutate(selectedEnabledRows)}
                  disabled={selectedEnabledRows.length === 0}
                  okText="Yes, Remove"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancel"
                >
                  <Button
                    type="primary"
                    danger
                    icon={<MinusOutlined />}
                    disabled={selectedEnabledRows.length === 0}
                    loading={removeTargetsMutation.isPending}
                  >
                    Remove Selected ({selectedEnabledRows.length})
                  </Button>
                </Popconfirm>
              )}
            </Col>
          </Row>

          <Table
            columns={targetColumns}
            dataSource={filteredEnabled}
            rowKey="id"
            loading={targetsLoading}
            rowSelection={
              canModifyTargets
                ? {
                    selectedRowKeys: selectedEnabledRows,
                    onChange: (keys) => setSelectedEnabledRows(keys as string[]),
                  }
                : undefined
            }
            pagination={{
              pageSize: 10,
              showTotal: (total) => `${total} enabled target${total !== 1 ? 's' : ''}`,
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <>
                      <p>No targets are currently enabled for this feature</p>
                      {canModifyTargets && (
                        <p style={{ color: '#888' }}>
                          Use the search above to find and add{' '}
                          {flag?.scope === 'user' ? 'users' : 'hospitals'}
                        </p>
                      )}
                    </>
                  }
                />
              ),
            }}
          />
        </Space>
      </Card>
    </div>
  )
}
